import { ShieldIcon } from "@xola/icons";
import {
    DateFormat,
    formatDate,
    getPartnerFeeByTraveler,
    getPaymentLineItems,
    isCancellationProtectionLineItem,
    isMistPurchase,
} from "common";
import { sumBy } from "lodash";
import React from "react";
import { ExperiencePurchaseItem, LineItem, LineItemType, Purchase, PurchaseItem } from "types";
import { Breakdown } from "@/components/Common/Breakdown";
import { useUser } from "@/hooks/useAccount";
import { getItemTotal } from "@/models/Purchase";
import { isXolaAdmin } from "@/models/User";

interface CancellationProtectionSummaryProps {
    purchase: Purchase;
    item?: PurchaseItem;
}

interface ItemLine {
    id: string;
    name: string;
    shortCode?: string;
    amount: number;
    fee: number;
}

type SummaryRow = ItemLine | { id: string; name: string; amount: number };

const withShortCode = (name: string, shortCode?: string) => (shortCode ? `${name} (${shortCode.toUpperCase()})` : name);

export const CancellationProtectionSummary = ({ purchase, item }: CancellationProtectionSummaryProps) => {
    const user = useUser();

    if (!isXolaAdmin(user)) {
        return null;
    }

    const isItemView = Boolean(item);
    const items = (isItemView ? [item] : purchase.items) as ExperiencePurchaseItem[];

    if (!items.some((item) => getItemFee(item) > 0)) {
        return null;
    }

    const rows = getSummaryRows(purchase, items, isItemView);

    // Payments belong to the purchase; an item only owns them when it is the purchase's only item.
    const showPayments = !isItemView || purchase.items.length === 1;
    const total =
        isItemView && "fee" in rows[0] ? rows[0].amount + rows[0].fee : purchase.amounts?.traveler ?? purchase.amount;

    return (
        <div className="h-max rounded-lg bg-primary-lighter p-6" data-testid="cancellation-protection-summary">
            <div className="mb-4 flex items-center text-lg font-bold">
                <ShieldIcon size="medium" className="mr-2" />
                Cancellation Protection
            </div>

            <Breakdown currency={purchase.currency}>
                {rows.map((row) => (
                    <React.Fragment key={row.id}>
                        <Breakdown.Item value={row.amount}>
                            {"shortCode" in row ? withShortCode(row.name, row.shortCode) : row.name}
                        </Breakdown.Item>
                        {"fee" in row && row.fee > 0 && (
                            <Breakdown.Item value={row.fee} color="primary">
                                {withShortCode("Cancellation Protection Fee", row.shortCode)}
                            </Breakdown.Item>
                        )}
                    </React.Fragment>
                ))}
                <Separator />
                <Breakdown.SubtotalItem info="Total" value={total} />
            </Breakdown>

            {showPayments && <PaymentRows purchase={purchase} />}
        </div>
    );
};

const getItemFee = (item: ExperiencePurchaseItem) =>
    sumBy(item.lineItems?.filter(isCancellationProtectionLineItem) ?? [], "amount");

const getSummaryRows = (purchase: Purchase, items: ExperiencePurchaseItem[], isItemView: boolean): SummaryRow[] => {
    const rows: SummaryRow[] = items.map((item) => ({
        id: item.id,
        name: item.name,
        shortCode: isMistPurchase(purchase) ? item.shortCode : undefined,
        amount: getItemTotal(purchase, item),
        fee: getItemFee(item),
    }));

    const partnerFee = getPartnerFeeByTraveler(purchase);

    // The mist root reads as a full purchase summary, so the partner fee row keeps the rows summing to Total.
    if (!isItemView && isMistPurchase(purchase) && partnerFee) {
        rows.push({
            id: partnerFee.id,
            name: partnerFee.name,
            amount: partnerFee.amounts?.traveler ?? partnerFee.amount,
        });
    }

    return rows;
};

// Breakdown.Separator draws in gray-light, which all but disappears on this card's blue.
const Separator = () => (
    <tr>
        <td colSpan={3} className="border-b border-primary-medium pb-1" />
    </tr>
);

interface PaymentRowsProps {
    purchase: Purchase;
}

const getPaymentRowName = (lineItem: LineItem) =>
    lineItem.type === LineItemType.REFUND_COMMISSION ? "Service fee refund" : lineItem.name;

const PaymentRows = ({ purchase }: PaymentRowsProps) => {
    const paymentLineItems = getPaymentLineItems(purchase).filter(
        (lineItem) => !(lineItem.type === LineItemType.REFUND_COMMISSION && lineItem.amount === 0),
    );

    if (paymentLineItems.length === 0) {
        return null;
    }

    const paid = -1 * sumBy(paymentLineItems, (lineItem) => lineItem.amounts?.traveler ?? lineItem.amount);

    return (
        <Breakdown currency={purchase.currency} className="mt-4">
            {paymentLineItems.map((lineItem) => (
                <Breakdown.Item
                    key={lineItem.id}
                    secondary={formatDate(lineItem.createdAt, DateFormat.DATE_SHORT)}
                    value={-1 * (lineItem.amounts?.traveler ?? lineItem.amount)}
                >
                    {getPaymentRowName(lineItem)}
                </Breakdown.Item>
            ))}
            <Separator />
            <Breakdown.SubtotalItem info="Customer Paid" value={paid} />
        </Breakdown>
    );
};
